import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import Image1 from './images/Image-1_7.jpg';
import Image2 from './images/Image-2_7.jpg';
import Image3 from './images/Image-3_7.jpg';
import Image4 from './images/Image-4.jpg';
import Image5 from './images/Image-5.jpg';
import Image6 from './images/Image-6.jpg';
import Image7 from './images/Image-7.jpg';

import styles from './styles.module.scss';
import { openCreateComponentUI } from '../../../../../../modules/editor/modules/build/modules/create/actions';
import { getAllCourses, getAllCourseEnrollments } from '../../api';

// eslint-disable-next-line react/prop-types
const Features = ({ isAppAdmin = false, appname, history }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isSubAdminWithMulipleApps = useSelector(
    state =>
      (state.account.auth.profile.admin_appnames &&
        Object.keys(state.account.auth.profile.admin_appnames).length > 1) ||
      false,
  );

  const handleClick = () => {
    history.push(`/${appname}/editor/build/edit?page=homepage`);
    dispatch(openCreateComponentUI('Online Course'));
  };

  // Prefetch courses data in the background when Features page loads
  // This ensures data is ready when user navigates to the courses page
  useEffect(() => {
    if (appname) {
      // Prefetch courses data
      queryClient.prefetchQuery(
        ['courseItems', { appname }],
        getAllCourses,
        {
          staleTime: 30000, // Consider data fresh for 30 seconds
          cacheTime: 300000, // Keep unused data in cache for 5 minutes
        },
      );

      // Prefetch enrollments data
      queryClient.prefetchQuery(
        ['courseEnrollments', { appname }],
        getAllCourseEnrollments,
        {
          staleTime: 30000, // Consider data fresh for 30 seconds
          cacheTime: 300000, // Keep unused data in cache for 5 minutes
        },
      );
    }
  }, [appname, queryClient]);

  return (
    <div style={{ overflow: 'scroll', padding: 0 }}>
      <div className={styles.container_top}>
        <p>
          <Trans>
            The Online Courses feature works alongside the Online Courses
            component, which you can add from the
          </Trans>{' '}
          <a
            style={{ color: '#261ed5', fontWeight: 'bold', cursor: 'pointer' }}
            onClick={handleClick}
          >
            <Trans>Components menu</Trans>
          </a>{' '}
          <strong>
            <Trans>(Build ➔ Pages ➔ Add Component)</Trans>
          </strong>
          .{' '}
          <Trans>
            Courses created in the Online Courses section will be displayed in
            your Online Courses component on the selected app page.
          </Trans>
        </p>

        {!isAppAdmin && !isSubAdminWithMulipleApps && (
          <p>
            <Trans>Check out our article</Trans>{' '}
            <a
              href="https://beezer.zendesk.com/hc/en-us/articles/33975488638225-Online-Courses-How-to-Set-Up-Courses"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#261ed5', fontWeight: 'bold' }}
            >
              <Trans>article</Trans>
            </a>{' '}
            <Trans>
              to learn more about how the Online Courses functionality works.
            </Trans>
          </p>
        )}
      </div>
      <div className={styles.container_middle}>
        <h1>
          <Trans>CORE FUNCTIONALITIES</Trans>
        </h1>
        <p>
          • <Trans>CREATE & UPDATE COURSES</Trans>
        </p>
        <p>
          • <Trans>ENABLE COURSE PAYMENTS</Trans>
        </p>
        <p>
          • <Trans>ADD TAX, CURRENCY, & COUPON</Trans>
        </p>
        <p>
          • <Trans>COLLECT DATA & TRACK PROGRESS</Trans>
        </p>
        <p>
          • <Trans>RECEIVE EMAIL UPDATES</Trans>
        </p>
      </div>
      <div className={styles.container_bottom}>
        <div className={styles.card}>
          <div>
            <h3>
              <Trans>ONLINE COURSES</Trans>
            </h3>
            <p>
              <Trans>
                Online Courses is your central hub for managing all your
                courses. You can easily create new courses and use filters to
                quickly find a specific one. To get started, navigate to
              </Trans>{' '}
              <strong>
                <Trans>Business Tools ➔ Online Courses</Trans>
              </strong>
              .
            </p>
          </div>
          <img className={styles.image} src={Image1} alt="Online course" />
        </div>
        <div className={styles.card_reverse}>
          <div>
            <h3>
              <Trans>PARTICIPANTS</Trans>
            </h3>
            <p>
              <Trans>
                Easily view who’s enrolled in your courses and track their
                progress at a glance - check enrollment details, completion
                status, and modules completed. You can also download participant
                data as a CSV file.
              </Trans>
            </p>
          </div>
          <img className={styles.image} src={Image2} alt="participant" />
        </div>
        <div className={styles.card}>
          <div>
            <h3>
              <Trans>COUPONS, TAX & MORE</Trans>
            </h3>
            <p>
              <Trans>
                <Trans>In</Trans> <strong>Settings</strong>,{' '}
                <Trans>
                  you’ll need to enter your Stripe API keys to connect the app
                  to your Stripe Business account and enable payment processing
                  for paid online courses.
                </Trans>
              </Trans>
            </p>
            <p>
              <Trans>
                You can also configure a variety of additional features,
                including optional tax settings, your preferred payment
                currency, and discount coupons for your customers.
              </Trans>
            </p>
          </div>
          <img className={styles.image} src={Image3} alt="settings" />
        </div>

        <div className={styles.container_appshell}>
          <h1>
            <Trans>APP PREVIEW</Trans>
          </h1>

          <p>
            <Trans>
              Below is an example of what an app featuring the Online Courses
              component could look like.
            </Trans>
          </p>

          <div>
            <img src={Image4} alt="Online Course Image4" />
            <img src={Image5} alt="Online Course Image5" />
            <img src={Image6} alt="Online Course Image6" />
            <img src={Image7} alt="Online Course Image6" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
